import React, { useRef, useEffect, useState, useCallback } from 'react';
import Lottie from 'lottie-react';
import CountUp from 'react-countup';
import { toast } from 'react-toastify';
import confettiJson from '../../../assets/animations/Confetti.json';
import CeLogo from '../../../assets/images/CE-logo.jpg';
import { useBall, BALLS, setBallIndex } from '../../Cockpit/ballThemes';
import { PokeBall } from '../../Cockpit/Objects3D';
import { getMarketName, getMarketByMarketIndex } from '../../../utils/marketUtils';
import { getTradeIndexNumber } from '../../../utils/tradeNavigation';
import { toPng, toBlob } from 'html-to-image';
import { generateShareableUrl } from '../../../utils/shareableTrade';

/**
 * Trade Result Card
 *
 * This is the one surface in the app that leaves the app: it is rasterised to
 * PNG and posted somewhere else. Two consequences shape everything below.
 *
 * 1. It cannot read `var(--token)`. html-to-image serialises computed styles
 *    into a foreignObject, and a card that resolves its palette from :root is
 *    a card that renders differently depending on where it was captured. The
 *    palette is therefore literal, declared once in `P`.
 *
 * 2. It is dark regardless of the app's theme. This image is posted to feeds
 *    and chats, not read inside the app, and a single fixed treatment is what
 *    makes a wall of these look like one exchange rather than two.
 *
 * The chrome around the card (backdrop, action bar) is ordinary app UI and does
 * use the token layer.
 */

// ─── Palettes ───

const BASE = {
  ground: '#0B0D12',
  wash: 'linear-gradient(160deg, rgba(255,255,255,.05) 0%, rgba(255,255,255,0) 46%)',
  edge: '1px solid rgba(255,255,255,.09)',
  tile: 'rgba(255,255,255,.045)',
  tileEdge: '1px solid rgba(255,255,255,.07)',
  tileShadow: 'inset 0 1px 0 rgba(255,255,255,.06)',
  ink: '#FFFFFF',
  ink2: 'rgba(255,255,255,.62)',
  ink3: 'rgba(255,255,255,.40)',
  line: 'rgba(255,255,255,.10)',
  chip: 'rgba(255,255,255,.09)',
  up: '#3FC47E',
  down: '#FF6B6E',
  upSoft: 'rgba(63,196,126,.13)',
  downSoft: 'rgba(255,107,110,.13)',
  figureTop: '#FFFFFF',
  orb: 0.3,
};

/**
 * The Pokémon treatment.
 *
 * Only POKE and PSA-10 get it. Both markets price graded Pokémon cards, and
 * the app already renders them inside the Pokédex cockpit — so a result card
 * for one is that device, not a holo card: moulded panel faces, 3px black
 * outlines, hard unblurred drop shadows and a lit screen, exactly the language
 * `cockpit.css` speaks. It recolours with the selected ball, so the card a
 * user shares matches the device they traded on.
 *
 * The values below are `cockpit.css`'s `--pd-*` written out. The card cannot
 * read a token and still rasterise identically wherever it is captured; the
 * ball descriptor is literal hex already, which is what makes this possible.
 */
const POKEMON_MARKET_IDS = new Set(['poke', 'psa-10']);

const PD_BLACK = '#1b1b1b';

const pokePalette = (ball) => ({
  // The device casing is the card's ground, the way it is the page's.
  ground: ball.casing,
  panel: ball.panel,
  screen: ball.screen,
  wash: 'none',
  edge: `3px solid ${PD_BLACK}`,
  tile: ball.slot,
  tileEdge: `2px solid ${PD_BLACK}`,
  tileShadow: 'inset 2px 2px 0 rgba(27,27,27,.1)',
  ink: PD_BLACK,
  ink2: '#5c5c5c',
  ink3: '#9a9a9a',
  line: ball.slot2,
  chip: ball.slot2,
  up: '#16b57f',
  down: '#e64646',
  upSoft: 'rgba(22,181,127,.16)',
  downSoft: 'rgba(230,70,70,.16)',
  accentInk: ball.accentInk,
  // Nothing on this device glows or gradients. The figure is one flat colour
  // and the ground is one flat colour — that is what makes it read as moulded
  // plastic rather than as glass.
  flat: true,
  figureTop: null,
  orb: 0,
});

// ─── Helpers ───

/** Contract timestamps arrive as seconds, milliseconds or nanoseconds. */
const toMillis = (timestamp) => {
  if (timestamp === null || timestamp === undefined || timestamp === '') return null;
  const n = Number(timestamp);
  if (isNaN(n) || n <= 0) {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d.getTime();
  }
  if (n.toString().length >= 19) return Math.floor(n / 1000000);
  if (n > 1e12) return n;
  return n * 1000;
};

/** "2d 4h", "3h 12m", "18m" — the shape people actually quote a hold in. */
const formatDuration = (ms) => {
  if (!ms || ms < 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

const TradeResultCard = ({ trade, onClose, onShare }) => {
  const cardRef = useRef(null);
  // Unconditional: the card may not be a Pokémon one, but a hook cannot be.
  const ball = useBall();
  const [showAnimation, setShowAnimation] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  // Calculate trade metrics
  const marketName = getMarketName(trade.market_index);
  const isPoke = POKEMON_MARKET_IDS.has(getMarketByMarketIndex(trade.market_index)?.id);
  const P = isPoke ? pokePalette(ball) : BASE;
  const entryPrice = parseFloat(trade.open_price) || 0;
  const closePrice = trade.trade_info?.closed_price || trade.close_price || 0;
  const collateralAmount = parseFloat(trade.collateral_amount) / 1000000;
  const leverage = parseFloat(trade.leverage) || 1;
  const isLong = trade.long;
  const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);

  // PnL Calculations
  const positionSize = collateralAmount * leverage;
  const assetQuantity = positionSize / entryPrice;

  let pnlUsd;
  if (isLong) {
    pnlUsd = (closePrice - entryPrice) * assetQuantity;
  } else {
    pnlUsd = (entryPrice - closePrice) * assetQuantity;
  }

  const pnlPercentage = collateralAmount > 0 ? (pnlUsd / collateralAmount) * 100 : 0;
  const isProfit = pnlUsd > 0;

  // How far the mark actually travelled, independent of leverage. On a 50x
  // position the headline percentage says nothing about the market — this does.
  const priceMovePct = entryPrice > 0 ? ((closePrice - entryPrice) / entryPrice) * 100 : 0;

  const accent = isProfit ? P.up : P.down;
  const accentSoft = isProfit ? P.upSoft : P.downSoft;

  // ─── Timing ───
  const closedMs =
    toMillis(trade.trade_info?.closed_at) ??
    toMillis(trade.closed_at) ??
    toMillis(trade.trade_info?.last_oi_update_ts) ??
    toMillis(trade.close_timestamp) ??
    Date.now();
  const openedMs = toMillis(trade.open_timestamp);
  const heldFor = openedMs && closedMs > openedMs ? formatDuration(closedMs - openedMs) : null;

  const closedDate = new Date(closedMs);
  const closedTime = isNaN(closedDate.getTime())
    ? ''
    : closedDate.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
  const closedLabel = isNaN(closedDate.getTime())
    ? ''
    : `${closedDate.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })} · ${closedTime}`;
  // No room for the year on a phone, and the year is the least useful part.
  const closedLabelShort = isNaN(closedDate.getTime())
    ? ''
    : `${closedDate.toLocaleDateString('en-US', { day: '2-digit', month: 'short' })} · ${closedTime}`;

  // Formatters
  const formatPrice = (price) => {
    if (!price) return '$0.00';
    return `$${parseFloat(price).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatCurrency = (value) => {
    const abs = Math.abs(Number(value) || 0);

    // For normal-sized PnL (>= 1), keep 2 decimals for a clean look
    if (abs >= 1) {
      return `$${abs.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    // For very small PnL (< 1), increase decimals until non-zero, up to 6
    let decimals = 2;
    while (decimals < 6 && Number(abs.toFixed(decimals)) === 0) {
      decimals += 1;
    }

    return `$${abs.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAnimation(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  // Escape closes, like every other overlay in the app.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownload = async () => {
    if (!cardRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      await document.fonts.ready;

      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 4,
        backgroundColor: null,
      });

      const link = document.createElement('a');
      link.download = `CodedEstate-${marketName}-${isProfit ? 'Win' : 'Loss'}-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();

      // Show success toast
      toast.success('Image downloaded successfully!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      if (onShare) {
        onShare(dataUrl);
      }
    } catch (error) {
      console.error('Error generating image:', error);
      // Show error toast
      toast.error('Failed to download. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopy = async () => {
    if (!cardRef.current) return;

    try {
      const blob = await toBlob(cardRef.current, {
        cacheBust: true,
        pixelRatio: 4,
        backgroundColor: null,
      });

      if (blob) {
        window.focus();
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        setIsCopied(true);

        // Show success toast
        toast.success('Image copied to clipboard!', {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setTimeout(() => setIsCopied(false), 2000);
      } else {
        throw new Error('Failed to generate image blob');
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      // Show error toast
      toast.error('Failed to copy. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleShareLink = async () => {
    try {
      const shareUrl = generateShareableUrl(trade);

      if (!shareUrl) {
        throw new Error('Failed to generate shareable URL');
      }

      await navigator.clipboard.writeText(shareUrl);
      setIsLinkCopied(true);

      // Show success toast
      toast.success('Share link copied to clipboard!', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setTimeout(() => setIsLinkCopied(false), 2000);
    } catch (error) {
      console.error('Error copying share link:', error);
      toast.error('Failed to copy link. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  }, [onClose]);

  // ─── Sub-components ───

  /** A stat tile. Label over value, one fact each — the shape the app uses. */
  const Stat = ({ label, value, sub, valueColor }) => (
    <div
      className={`min-w-0 ${P.flat ? 'px-3 py-2 sm:px-3.5 sm:py-2.5' : 'px-3 py-2.5 sm:px-3.5 sm:py-3'}`}
      style={{
        background: P.tile,
        border: P.tileEdge,
        borderRadius: P.flat ? 10 : 14,
        boxShadow: P.tileShadow,
      }}
    >
      <p
        className="text-[9px] sm:text-[10px] uppercase mb-1 truncate"
        style={{ color: P.ink3, letterSpacing: '.09em', fontWeight: 600 }}
      >
        {label}
      </p>
      <div
        className="text-[14px] sm:text-[16px] font-bold truncate"
        style={{ color: valueColor || P.ink, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
      {sub && (
        <p className="text-[10px] sm:text-[11px] mt-0.5 truncate" style={{ color: P.ink3 }}>
          {sub}
        </p>
      )}
    </div>
  );

  return (
    <div
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Trade result card"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 animate-fadeIn overflow-y-auto"
      style={{ background: 'rgba(6,7,10,.74)', backdropFilter: 'blur(10px)' }}
    >
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" crossOrigin="anonymous" />

      {/* Global Animation Overlay */}
      {showAnimation && (
        <div className="fixed inset-0 pointer-events-none z-[10000] overflow-hidden">
          <Lottie
            animationData={confettiJson}
            loop={false}
            className="absolute inset-0 w-full h-full object-cover opacity-80"
            style={{
              filter: isProfit ? 'none' : 'grayscale(100%) sepia(100%) saturate(600%) hue-rotate(-50deg)'
            }}
          />
        </div>
      )}

      <div className="flex flex-col items-center gap-3 sm:gap-4 w-full min-w-0 max-w-[440px] my-auto">

        {/* ─── Chrome ───
            The balls live here rather than on the card: they retint the whole
            cockpit, not just this surface, and they must not be baked into the
            export. Pokémon markets only — every other card has one treatment. */}
        <div className="flex items-center justify-between gap-2 w-full">
          {isPoke ? (
            <div
              /* `ce-raw` for the same reason the card carries it: theme.css's
                 dark-mode shims rewrite inline gradients to tokens, which
                 flattened all four balls into identical dark discs. */
              className="ce-raw flex items-center gap-1.5 p-1.5"
              style={{
                background: 'var(--surface)',
                borderRadius: 'calc(var(--r-control) + 6px)',
                boxShadow: 'var(--card)',
              }}
            >
              {BALLS.map((b, i) => (
                <button
                  key={b.key}
                  onClick={() => setBallIndex(i)}
                  aria-label={b.name}
                  aria-pressed={b.key === ball.key}
                  title={b.name}
                  className="flex items-center justify-center flex-shrink-0"
                  style={{
                    height: 34,
                    width: 34,
                    padding: 0,
                    borderRadius: 999,
                    border: 'none',
                    cursor: 'pointer',
                    // Selection has to survive on a 34px target, so it is
                    // carried three ways at once: a filled well, a size step,
                    // and the unselected balls held back in opacity.
                    background: b.key === ball.key ? 'var(--surface-2, rgba(127,127,140,.2))' : 'transparent',
                    boxShadow: b.key === ball.key ? 'var(--inset, inset 2px 2px 5px rgba(0,0,0,.18))' : 'none',
                    // Only a light hold-back: at 22px the balls are told apart
                    // by their markings, and dimming them muddied the colours
                    // that do that work.
                    opacity: b.key === ball.key ? 1 : 0.82,
                    transition: 'background .18s ease, transform .18s ease, opacity .18s ease',
                    transform: b.key === ball.key ? 'scale(1.1)' : 'none',
                  }}
                >
                  <PokeBall d={22} ball={b} />
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}
          <button
            onClick={onClose}
            aria-label="Close"
            className="ce-btn ce-btn-surface flex-shrink-0"
            style={{ height: 38, width: 38, padding: 0, borderRadius: 'var(--r-control)' }}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ─── The card ─── */}
        <div
          ref={cardRef}
          /* `ce-raw` is the design system's opt-out from the dark-mode legacy
             shims in theme.css. Those rules rewrite inline gradients and hex
             colours to tokens, which is right for app chrome and wrong for a
             surface that has to rasterise identically wherever it is captured
             — one of them was flattening the headline's gradient to a solid
             surface colour. */
          className="ce-raw relative w-full min-w-0 max-w-full rounded-[22px] sm:rounded-[26px] overflow-hidden flex flex-col"
          style={{
            background: P.ground,
            border: P.edge,
            aspectRatio: '4 / 5',
            padding: isPoke ? 8 : 0,
            fontFamily: "'Outfit', 'General Sans', sans-serif",
            boxShadow: '0 30px 80px rgba(0,0,0,.55)',
          }}
        >
        {/* The panel face the device is moulded with. On a non-Pokémon card
            this collapses to a zero-padding passthrough over the ground. */}
        <div
          className="relative flex-1 min-h-0 flex flex-col overflow-hidden"
          style={{
            background: isPoke ? P.panel : P.ground,
            border: isPoke ? `3px solid ${PD_BLACK}` : 'none',
            borderRadius: isPoke ? 12 : 0,
            boxShadow: isPoke ? '4px 4px 0 rgba(0,0,0,.28)' : 'none',
          }}
        >
          {/* Ground treatment. The outcome hue washes in from the top-right
              rather than tinting the whole card — a card that is 40% green
              before you have read the number is shouting the result twice. */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: P.wash }} />
          <div
            className="absolute pointer-events-none"
            style={{
              display: P.orb ? 'block' : 'none',
              top: '-22%',
              right: '-24%',
              width: '72%',
              height: '46%',
              borderRadius: '50%',
              background: accent,
              filter: 'blur(90px)',
              opacity: P.orb,
            }}
          />

          {/* Noise */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              opacity: isPoke ? 0 : 0.035,
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />

          {/* The one place the outcome colour is allowed to be flat and certain:
              a rail along the bottom edge, under everything. */}
          <div
            className="absolute bottom-0 left-0 right-0 pointer-events-none"
            style={{
              height: 3,
              display: isPoke ? 'none' : 'block',
              background: `linear-gradient(90deg, transparent 0%, ${accent} 22%, ${accent} 78%, transparent 100%)`,
              opacity: 0.85,
            }}
          />

          {/* Content */}
          <div className={`relative z-10 flex flex-col h-full ${isPoke ? 'p-3 sm:p-5' : 'p-4 sm:p-6'}`}>

            {/* ── Brand row ── */}
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div
                className={`flex items-center justify-center overflow-hidden flex-shrink-0 ${
                  isPoke ? 'w-8 h-8 sm:w-10 sm:h-10' : 'w-9 h-9 sm:w-11 sm:h-11'
                }`}
                style={{
                  borderRadius: isPoke ? 8 : 12,
                  background: P.tile,
                  border: P.tileEdge,
                  boxShadow: P.tileShadow,
                }}
              >
                <img src={CeLogo} alt="Coded Estate" className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <h3
                  className="font-bold text-[15px] sm:text-[17px] leading-none tracking-tight truncate"
                  style={{ color: P.ink }}
                >
                  Coded Estate
                </h3>
                <p
                  className="text-[9px] sm:text-[10px] mt-1 uppercase truncate"
                  style={{ color: P.ink3, letterSpacing: '.14em', fontWeight: 600 }}
                >
                  Perpetual Exchange
                </p>
              </div>
              {isPoke && (
                <span className="flex items-center gap-1 flex-shrink-0" aria-hidden="true">
                  {['#e44b4b', '#f2c438', '#4caf50'].map((led) => (
                    <span
                      key={led}
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        background: led,
                        border: `1.5px solid ${PD_BLACK}`,
                      }}
                    />
                  ))}
                </span>
              )}
              <span
                className="text-[10px] sm:text-[11px] px-2.5 py-1 whitespace-nowrap flex-shrink-0"
                style={{
                  color: P.ink2,
                  background: P.tile,
                  border: P.tileEdge,
                  borderRadius: 999,
                  boxShadow: P.tileShadow,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <span className="hidden sm:inline">{closedLabel}</span>
                <span className="sm:hidden">{closedLabelShort}</span>
              </span>
            </div>

            {/* ── Market line ──
                Pair, side and leverage belong together and belong up top: they
                are what the number below is a result *of*. They used to be two
                separate tiles in the grid, which buried the side chip under the
                market name and left this band empty. */}
            <div
              className={`flex items-center gap-2 min-w-0 ${
                isPoke ? 'mt-2.5 sm:mt-4 px-3 py-2 sm:px-3.5 sm:py-2.5' : 'mt-4 sm:mt-5 px-3 py-2.5 sm:px-3.5 sm:py-3'
              }`}
              style={{
                background: P.tile,
                border: P.tileEdge,
                borderRadius: isPoke ? 10 : 16,
                boxShadow: P.tileShadow,
              }}
            >
              {isPoke && (
                <span
                  className="flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    background: P.chip,
                    border: `2px solid ${PD_BLACK}`,
                  }}
                >
                  <PokeBall d={20} ball={ball} />
                </span>
              )}
              <span
                className="text-[15px] sm:text-[18px] font-bold truncate min-w-0"
                style={{ color: P.ink, letterSpacing: '-.01em' }}
              >
                {marketName}
              </span>
              <span className="flex items-center gap-1.5 ml-auto flex-shrink-0">
                <span
                  className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold pl-1.5 pr-2 py-0.5"
                  style={{
                    borderRadius: 999,
                    background: isLong ? P.upSoft : P.downSoft,
                    color: isLong ? P.up : P.down,
                    letterSpacing: '.05em',
                  }}
                >
                  <svg width="9" height="9" viewBox="0 0 10 10" className="flex-shrink-0" style={{ transform: isLong ? 'none' : 'rotate(180deg)' }}>
                    <path d="M5 1l4 7H1z" fill="currentColor" />
                  </svg>
                  {isLong ? 'LONG' : 'SHORT'}
                </span>
                <span
                  className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5"
                  style={{ borderRadius: 999, background: P.chip, color: P.ink2 }}
                >
                  {leverage}x
                </span>
              </span>
            </div>

            {/* ── The result ── */}
            <div
              className="relative flex flex-col items-center justify-center flex-1 min-h-0 overflow-hidden"
              style={isPoke ? {
                background: P.screen,
                border: `3px solid ${PD_BLACK}`,
                borderRadius: 6,
                boxShadow: 'inset 2px 2px 0 rgba(27,27,27,.1)',
                marginTop: 8,
                marginBottom: 8,
                paddingTop: 8,
                paddingBottom: 8,
              } : { paddingTop: 8, paddingBottom: 8 }}
            >
              {/* The device's standby mark. `PokeBall` is the cockpit's own
                  emblem, so Ultra reads as Ultra and Master carries its M —
                  a generic disc tinted with the dome colour did not. */}
              {isPoke && (
                <div
                  className="absolute pointer-events-none"
                  style={{ right: 10, top: 10, opacity: 0.22 }}
                  aria-hidden="true"
                >
                  <PokeBall d={44} ball={ball} />
                </div>
              )}
              <div className="relative z-10 flex flex-col items-center">
              <div
                className={`inline-flex items-center gap-1.5 px-3 py-1 ${isPoke ? 'mb-1.5' : 'mb-2 sm:mb-3'}`}
                style={{
                  borderRadius: 999,
                  background: accentSoft,
                  color: accent,
                  border: isPoke ? `2px solid ${PD_BLACK}` : 'none',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" className="flex-shrink-0" style={{ transform: isProfit ? 'none' : 'rotate(180deg)' }}>
                  <path d="M5 0.8l4.2 7.4H0.8z" fill="currentColor" />
                </svg>
                <span className="text-[10px] sm:text-[11px] font-bold uppercase" style={{ letterSpacing: '.09em' }}>
                  {isProfit ? 'Profit' : 'Loss'}
                </span>
              </div>

              <h1
                id="pnl-percentage"
                className={`relative font-black leading-[0.95] tracking-tighter ${
                  isPoke ? 'text-[36px] sm:text-[54px]' : 'text-[46px] sm:text-[66px]'
                }`}
                style={P.flat ? {
                  color: accent,
                  fontVariantNumeric: 'tabular-nums',
                } : {
                  backgroundImage: `linear-gradient(180deg, ${P.figureTop} 0%, ${accent} 88%)`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <CountUp
                  end={pnlPercentage}
                  decimals={2}
                  suffix="%"
                  prefix={pnlPercentage > 0 ? '+' : ''}
                  duration={2.5}
                  separator=","
                />
              </h1>

              <div className="flex items-baseline gap-1.5 mt-1.5">
                <span
                  className="text-[20px] sm:text-[24px] font-bold"
                  style={{ color: P.ink, fontVariantNumeric: 'tabular-nums' }}
                >
                  {pnlUsd >= 0 ? '+' : '-'}{formatCurrency(pnlUsd)}
                </span>
                <span className="text-[11px] sm:text-[13px] font-medium" style={{ color: P.ink3 }}>USD</span>
              </div>

              {heldFor && (
                <p className="text-[11px] sm:text-[12px] mt-2" style={{ color: P.ink3 }}>
                  Held for {heldFor}
                </p>
              )}
              </div>
            </div>

            {/* ── Stats ──
                Four facts, not two. A percentage on a 50x position is
                meaningless without the margin it was earned on and the size it
                was earned with — those were missing entirely. */}
            {/* Entry and exit are one fact, not two: a price that moved. Reading
                them as a path — with the raw market move between them — is what
                lets the headline percentage be understood as leverage on top of
                a small move rather than as the move itself. */}
            <div
              className={`flex items-center gap-2 sm:gap-3 min-w-0 ${
                isPoke ? 'px-3 py-2 sm:px-4 sm:py-2.5' : 'px-3 py-2.5 sm:px-4 sm:py-3'
              }`}
              style={{
                background: P.tile,
                border: P.tileEdge,
                borderRadius: isPoke ? 10 : 16,
                boxShadow: P.tileShadow,
              }}
            >
              <div className="min-w-0 flex-1">
                <p
                  className="text-[9px] sm:text-[10px] uppercase mb-0.5 truncate"
                  style={{ color: P.ink3, letterSpacing: '.09em', fontWeight: 600 }}
                >
                  Entry
                </p>
                <div
                  className="text-[14px] sm:text-[17px] font-bold truncate"
                  style={{ color: P.ink, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatPrice(entryPrice)}
                </div>
              </div>

              <div className="flex flex-col items-center gap-1 flex-shrink-0 px-1">
                {/* Deliberately not accent-coloured. This is the market's move,
                    not the trade's result — a short that wins on a falling
                    price would otherwise print a green negative number. */}
                <span
                  className="text-[9px] sm:text-[10px] font-semibold whitespace-nowrap"
                  style={{ color: P.ink2, fontVariantNumeric: 'tabular-nums' }}
                >
                  {priceMovePct >= 0 ? '+' : ''}{priceMovePct.toFixed(2)}%
                </span>
                <svg width="56" height="9" viewBox="0 0 56 9" fill="none" className="flex-shrink-0">
                  <path d="M1 4.5h44" stroke={P.line} strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M46 1L54 4.5 46 8z" fill={accent} />
                </svg>
              </div>

              <div className="min-w-0 flex-1 text-right">
                <p
                  className="text-[9px] sm:text-[10px] uppercase mb-0.5 truncate"
                  style={{ color: P.ink3, letterSpacing: '.09em', fontWeight: 600 }}
                >
                  Exit
                </p>
                <div
                  className="text-[14px] sm:text-[17px] font-bold truncate"
                  style={{ color: P.ink, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatPrice(closePrice)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-2.5 mt-2 sm:mt-2.5">
              <Stat label="Margin" value={formatCurrency(collateralAmount)} sub="USDC" />
              <Stat
                label="Position Size"
                value={formatCurrency(positionSize)}
                sub={`${leverage}x leverage`}
              />
            </div>

            {/* ── Footer ── */}
            <div
              className={`flex items-end justify-between gap-3 ${
                isPoke ? 'mt-2 pt-2 sm:mt-3 sm:pt-3' : 'mt-3 sm:mt-4 pt-3 sm:pt-4'
              }`}
              style={{ borderTop: `1px solid ${P.line}` }}
            >
              <span
                className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-medium"
                style={{ color: P.ink3, fontVariantNumeric: 'tabular-nums' }}
              >
                {isPoke && (
                  <svg width="10" height="10" viewBox="0 0 12 12" className="flex-shrink-0" aria-hidden="true">
                    <path d="M6 0l1.6 3.7L11.6 4l-3 2.7.9 4-3.5-2.1L2.5 10.7l.9-4-3-2.7 4-.3z" fill={ball.accent} />
                  </svg>
                )}
                Trade #{tradeIndexNumber || '0000'}
              </span>
              <div className="text-right">
                <p
                  className="text-[8px] sm:text-[9px] uppercase"
                  style={{ color: P.ink3, letterSpacing: '.16em', fontWeight: 600 }}
                >
                  Trade with us
                </p>
                <p className="text-[11px] sm:text-[12px] font-bold" style={{ color: P.ink }}>
                  codedestate.com
                </p>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* ─── Action bar ───
            App chrome, not export surface — so it reads from the token layer
            like every other control instead of stacking translucent white.
            It sits on its own raised tray: a pillow shadow is built to catch
            light from a pale ground, and floating these buttons straight on the
            dark scrim turned every one of their highlights into a white halo. */}
        <div
          className="flex items-center gap-1.5 sm:gap-2 w-full p-2 overflow-hidden"
          style={{
            background: 'var(--surface)',
            borderRadius: 'calc(var(--r-inner) + 8px)',
            boxShadow: 'var(--card)',
          }}
        >
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="ce-btn ce-btn-accent flex-1 min-w-0 text-sm whitespace-nowrap"
            style={{ height: 46, borderRadius: 'var(--r-inner)', paddingLeft: 12, paddingRight: 12 }}
          >
            {isDownloading ? (
              <svg className="ce-spinning h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity=".3" />
                <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            ) : (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
            <span className="hidden sm:inline">Save Image</span>
            <span className="sm:hidden">Save</span>
          </button>

          <button
            onClick={handleCopy}
            aria-label="Copy card image"
            className="ce-btn ce-btn-surface flex-1 min-w-0 text-sm whitespace-nowrap"
            style={{ height: 46, borderRadius: 'var(--r-inner)', paddingLeft: 12, paddingRight: 12 }}
          >
            {isCopied ? (
              <>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24" style={{ color: 'var(--up)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <rect x="9" y="9" width="11" height="11" rx="2.5" />
                  <path d="M5 15V6a2 2 0 012-2h9" strokeLinecap="round" />
                </svg>
                Copy
              </>
            )}
          </button>

          <button
            onClick={handleShareLink}
            aria-label="Copy share link"
            className="ce-btn ce-btn-surface flex-1 min-w-0 text-sm whitespace-nowrap"
            style={{ height: 46, borderRadius: 'var(--r-inner)', paddingLeft: 12, paddingRight: 12 }}
          >
            {isLinkCopied ? (
              <>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24" style={{ color: 'var(--up)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span className="hidden sm:inline">Share Link</span>
                <span className="sm:hidden">Share</span>
              </>
            )}
          </button>

        </div>
      </div>
    </div>
  );
};

export default TradeResultCard;
